import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api';
import type { OfficeView } from '../api';
import { useAuth } from '../auth';

/**
 * Loads the admin-managed offices ("Agente responsable") once for the authenticated
 * session and exposes them to pickers + display. `code` is the stored reference; the
 * provider resolves it to the editable display name. Fetches only when authenticated
 * (avoids a 401 on the public login screen). `reload()` lets the admin Settings screen
 * refresh after a create/update/delete so every picker reflects the change immediately.
 */
interface OfficesContextValue {
  offices: OfficeView[];
  /** Active offices only — what pickers should offer. */
  activeOffices: OfficeView[];
  /** Resolve a stored office code to its display name (falls back to the code). */
  officeName: (code: string | null | undefined) => string;
  reload: () => void;
}

const OfficesContext = createContext<OfficesContextValue | null>(null);

export function OfficesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [offices, setOffices] = useState<OfficeView[]>([]);

  const load = useCallback(() => {
    if (!isAuthenticated) {
      setOffices([]);
      return;
    }
    void api.offices
      .list()
      .then((rows) => setOffices(rows))
      .catch(() => {
        /* best-effort; pickers degrade to an empty list */
      });
  }, [isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo<OfficesContextValue>(() => {
    const byCode = new Map(offices.map((o) => [o.code, o]));
    return {
      offices,
      activeOffices: offices.filter((o) => o.active),
      officeName: (code) => (code ? (byCode.get(code)?.name ?? code) : ''),
      reload: load,
    };
  }, [offices, load]);

  return <OfficesContext.Provider value={value}>{children}</OfficesContext.Provider>;
}

export function useOffices(): OfficesContextValue {
  const ctx = useContext(OfficesContext);
  if (!ctx) throw new Error('useOffices must be used within <OfficesProvider>');
  return ctx;
}
